import { useInfiniteQuery } from '@tanstack/react-query';
import { AI_CREDIT_TRANSACTION_KIND_LABELS } from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { QueryState } from '../QueryState.js';
import { EmptyState } from '../ui/EmptyState.js';
import { absoluteTime } from '../references/format.js';
import { creditAmount, signedCreditAmount } from './format.js';
import { creditTransactionsKey } from './useProjectCredits.js';

/**
 * EVERY CREDIT MOVEMENT ON THIS PRODUCTION, newest first, a page at a time (S4).
 *
 * One line per fact a producer recognises: a purchase, the initial credits, an
 * AI operation (its reservation and release are folded by the server into ONE
 * line showing what was charged), a refund, an adjustment. The amount is signed
 * as the server sent it and the balance after each line is the server's — the
 * history never re-adds a column to check itself.
 *
 * The label is the backend's; the kind's contract label is only the fallback,
 * so a relabel there is a copy change and never needs a deploy here.
 */
export function CreditTransactionHistory({ projectId }: { projectId: string }) {
  const history = useInfiniteQuery({
    queryKey: creditTransactionsKey(projectId),
    queryFn: ({ pageParam }) => api.creditTransactions(projectId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const items = history.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <section className="credits-history" data-testid="credit-history" aria-labelledby="credit-history-title">
      <h3 className="colh" id="credit-history-title" style={{ margin: 0 }}>
        Credit history
      </h3>

      {/* Above the list, never instead of it: a background refetch that fails
          must not blank out lines the producer is reading. */}
      <QueryState query={history} subject="the credit history" testId="credit-history" />

      {history.isSuccess && items.length === 0 ? (
        <EmptyState title="No credit movements yet" testId="credit-history-empty">
          Credits added to this production, and every AI operation charged to it, are listed here.
        </EmptyState>
      ) : null}

      {items.length > 0 ? (
        <div className="credits-table-wrap">
          <table className="credits-table" data-testid="credit-history-table">
            <thead>
              <tr>
                <th scope="col">Date and time</th>
                <th scope="col">Type</th>
                <th scope="col">Operation</th>
                <th scope="col" className="credits-num">
                  Amount
                </th>
                <th scope="col">Status</th>
                <th scope="col" className="credits-num">
                  Balance after
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} data-testid={`credit-transaction-${item.id}`} data-kind={item.kind}>
                  <td data-label="Date and time" data-testid="credit-transaction-date">
                    {absoluteTime(item.occurredAt)}
                  </td>
                  <td data-label="Type" data-testid="credit-transaction-label">
                    {item.label === '' ? AI_CREDIT_TRANSACTION_KIND_LABELS[item.kind] : item.label}
                  </td>
                  <td data-label="Operation" data-testid="credit-transaction-operation">
                    {item.operationTitle ?? <span className="mm">—</span>}
                  </td>
                  <td data-label="Amount" className="credits-num" data-testid="credit-transaction-amount">
                    {signedCreditAmount(item.amountCents)}
                  </td>
                  <td data-label="Status" data-testid="credit-transaction-status">
                    {item.status}
                  </td>
                  <td data-label="Balance after" className="credits-num" data-testid="credit-transaction-balance">
                    {creditAmount(item.balanceAfterCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {history.hasNextPage ? (
        <button
          type="button"
          className="ctl"
          style={{ marginTop: 12 }}
          data-testid="credit-history-more"
          disabled={history.isFetchingNextPage}
          onClick={() => {
            void history.fetchNextPage();
          }}
        >
          {history.isFetchingNextPage ? 'Loading…' : 'Show older'}
        </button>
      ) : null}
    </section>
  );
}
